import { forwardRef, AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react';
import cx from 'classnames';
import { Icon } from '@legacy/components/ui-components/Icon';
import { TIconName } from '@legacy/assets/variables/icon';
import { useGetLink } from '../../global/LinkProvider';

import styles from './Link.module.scss';

interface LinkBaseProps {
  iconName?: TIconName;
  useTextColor?: boolean;
  block?: boolean;
  bold?: boolean;
  underline?: boolean;
  inline?: boolean;
}

export interface TextLinkProps
  extends AnchorHTMLAttributes<HTMLAnchorElement>,
    LinkBaseProps {
  children?: React.ReactNode;
}

export interface LinkProps extends Omit<TextLinkProps, 'iconName'> {
  children?: React.ReactNode;
}

export const TextLink = forwardRef<HTMLAnchorElement, TextLinkProps>(
  (
    {
      children,
      iconName,
      href,
      useTextColor,
      block,
      bold,
      underline,
      inline,
      className,
      ...props
    },
    ref,
  ) => {
    const ActionLink = useGetLink();
    return (
      <ActionLink
        className={cx(styles.link, className, {
          [styles.hasIcon]: iconName,
          [styles.gray]: useTextColor,
          [styles.block]: block,
          [styles.bold]: bold,
          [styles.underline]: underline,
          [styles.inline]: inline,
        })}
        href={href}
        ref={ref}
        {...props}
      >
        {iconName && (
          <Icon
            iconName={iconName}
            className={styles.icon}
            aria-hidden="true"
          />
        )}
        <span>{children}</span>
      </ActionLink>
    );
  },
);

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      children,
      href,
      useTextColor,
      block,
      bold,
      underline,
      className,
      ...props
    },
    ref,
  ) => {
    const ActionLink = useGetLink();
    return (
      <ActionLink
        className={cx(styles.link, className, {
          [styles.gray]: useTextColor,
          [styles.block]: block,
          [styles.bold]: bold,
          [styles.underline]: underline,
        })}
        href={href}
        ref={ref}
        {...props}
      >
        {children}
      </ActionLink>
    );
  },
);

export interface LinkAsButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    LinkBaseProps {}

export const LinkAsButton = forwardRef<HTMLButtonElement, LinkAsButtonProps>(
  (
    { children, useTextColor, block, bold, underline, className, ...props },
    ref,
  ) => (
    <button
      type="button"
      className={cx(styles.link, styles.linkButton, className, {
        [styles.gray]: useTextColor,
        [styles.block]: block,
        [styles.bold]: bold,
        [styles.underline]: underline,
      })}
      ref={ref}
      {...props}
    >
      {children}
    </button>
  ),
);
